import SoundConfig = Phaser.Types.Sound.SoundConfig;
import Scene = Phaser.Scene;
import BaseSound = Phaser.Sound.BaseSound;
import SceneUtils from "@/utils/scene-utils";

export default class SoundUtils {

    // True once we've gone into the background and haven't yet successfully brought the
    // audio context back to a `running` state. While set, the next chance we get (a
    // `visibilitychange`, or — crucially on iOS — the next user gesture) tries to recover.
    private static pendingRecovery = false;

    // Guards installResumeGuards() against registering its listeners more than once.
    private static resumeGuardsInstalled = false;

    // Set while backgrounded if we ever observe the context in iOS's `interrupted` state.
    // That state means a real audio interruption happened and the context is now a zombie
    // (reports `running` after resume but produces no output) — the only cure is to build a
    // fresh AudioContext, so this flag arms recreateAudioContext() on the next gesture.
    private static sawInterrupted = false;

    public static soundOff() {
        // if (SceneUtils.getCurrentScene()) {
        //     SceneUtils.getCurrentScene().game.sound.mute = true;
        // }
        // We're going to the background: the browser/OS will suspend (iOS: *interrupt*)
        // the Web Audio context, so flag that a recovery is needed on the way back.
        SoundUtils.pendingRecovery = true;
        SoundUtils.sawInterrupted = false;
        SoundUtils.noteContextState();
        [].slice.call(document.querySelectorAll('audio')).forEach(function(audio) {
            // @ts-ignore
            audio.muted = true;
        });
    }

    public static soundOn() {
        // if (SceneUtils.getCurrentScene()) {
        //     SceneUtils.getCurrentScene().game.sound.mute = false;
        // }
        SoundUtils.noteContextState();
        [].slice.call(document.querySelectorAll('audio')).forEach(function(audio) {
            // @ts-ignore
            audio.muted = false;
        });
        // Immediate attempt on becoming visible. On iOS this often no-ops (resuming an
        // interrupted context needs a user gesture) — installResumeGuards() covers that.
        SoundUtils.resumeGameAudioContext();
    }

    /**
     * Registers one-time listeners that keep the game's Web Audio context alive.
     *
     * The core problem on iOS/WebKit: when the browser is backgrounded the audio context is
     * *interrupted*. Afterwards it is a zombie — even once it reports `running` again it
     * produces no output, and every sound (music and SFX) stays silent. The only reliable
     * cure is to replace the AudioContext, and iOS only lets a fresh context start from
     * within a user gesture. Since the game is tap-driven, we do that recovery on the next
     * pointer/touch/key event. Call this once, early (e.g. from MainView.init()).
     */
    public static installResumeGuards() {
        if (SoundUtils.resumeGuardsInstalled || typeof document === 'undefined') {
            return;
        }
        SoundUtils.resumeGuardsInstalled = true;
        const handler = () => {
            if (SoundUtils.pendingRecovery) {
                SoundUtils.noteContextState();
                // iOS zombie context: once we've seen `interrupted` this cycle, resuming the
                // same context yields silence — replace it. Must run inside this user gesture.
                if (SoundUtils.sawInterrupted && SoundUtils.isProbablyMobile()) {
                    SoundUtils.recreateAudioContext();
                    return;
                }
            }
            SoundUtils.resumeGameAudioContext();
        };
        // Capture + passive so we run early and never interfere with the game's own input.
        ['pointerdown', 'touchstart', 'touchend', 'mousedown', 'click', 'keydown'].forEach((evt) => {
            document.addEventListener(evt, handler, { capture: true, passive: true } as any);
        });
    }

    /**
     * Brings the game's Web Audio context back to `running` and, on success, repairs the
     * looping music. Safe to call as often as we like: when the context is already running
     * and no recovery is pending it does almost nothing, so wiring it to every user gesture
     * is cheap.
     */
    public static resumeGameAudioContext() {
        try {
            const soundManager: any = (window as any).phgame?.sound;
            if (!soundManager) {
                return;
            }
            // Re-enable Phaser's own per-frame safety-net resume in
            // WebAudioSoundManager.update() (it is gated behind !gameLostFocus). We only get
            // here from "the game is in the foreground" moments, so this is safe.
            soundManager.gameLostFocus = false;

            // If the context was interrupted at any point during this background cycle it's a
            // zombie now — even though iOS may have since moved it interrupted->suspended and
            // it would resume() to 'running', it stays silent. Do NOT let this (non-gesture)
            // path resume it and clear pendingRecovery; leave recovery for the next user
            // gesture, which recreates the context. Otherwise we'd "recover" a dead context.
            if (SoundUtils.sawInterrupted && SoundUtils.isProbablyMobile()) {
                return;
            }

            const context = soundManager.context;
            if (!context) {
                // No Web Audio (HTML5 Audio fallback): nothing to resume.
                SoundUtils.finishRecovery(soundManager);
                return;
            }
            if (context.state === 'interrupted') {
                // Can't resume an interrupted context (throws InvalidStateError); mark it so
                // the next gesture recreates the context instead.
                SoundUtils.sawInterrupted = true;
                return;
            }
            if (context.state === 'running') {
                SoundUtils.finishRecovery(soundManager);
                return;
            }
            if (typeof context.resume === 'function') {
                context.resume()
                    .then(() => SoundUtils.finishRecovery(soundManager))
                    .catch(() => { /* still interrupted — a later gesture will retry */ });
            }
        } catch (e) {
            // ignore – resuming sound is best-effort
        }
    }

    // Records whether the game's audio context is currently in iOS's `interrupted` state.
    private static noteContextState() {
        try {
            const ctx: any = (window as any).phgame?.sound?.context;
            if (ctx && ctx.state === 'interrupted') {
                SoundUtils.sawInterrupted = true;
            }
        } catch (e) { /* ignore */ }
    }

    /**
     * Replaces the game's Web Audio context with a brand-new one and rewires every sound
     * onto it. This is the only reliable cure for the iOS/WebKit "interrupted" bug, where
     * the old context keeps reporting `running` after an interruption but produces no
     * output. MUST be called from within a user gesture so iOS lets the new context start.
     */
    public static recreateAudioContext() {
        try {
            const sm: any = (window as any).phgame?.sound;
            if (!sm || !sm.context || typeof sm.setAudioContext !== 'function') {
                return;
            }
            const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!AC) { return; }

            let newCtx: any;
            try { newCtx = new AC(); } catch (e) { return; }

            // Clear the flags synchronously now that we're committed: a single tap fires
            // several gesture events (pointerdown/touchstart/…) and we must not recreate
            // the context more than once per background cycle.
            SoundUtils.pendingRecovery = false;
            SoundUtils.sawInterrupted = false;

            // Remember which looping sounds (the music) were playing so we can resume them,
            // capturing their volume now (before the node rebuild resets it), and preserve
            // the manager's mute/volume across the context swap.
            const sounds: any[] = Array.isArray(sm.sounds) ? sm.sounds.slice() : [];
            const toReplay = sounds
                .filter((s) => s && s.loop && s.isPlaying)
                .map((s) => ({ s, volume: typeof s.volume === 'number' ? s.volume : undefined }));
            const prevMute = sm.mute;
            const prevVolume = sm.volume;

            // setAudioContext closes the old context and rebuilds the master mute/volume
            // nodes (and sm.destination) on the new one.
            sm.setAudioContext(newCtx);
            try { sm.mute = prevMute; } catch (e) { /* ignore */ }
            try { sm.volume = prevVolume; } catch (e) { /* ignore */ }

            // Every existing sound still has nodes on the now-closed old context — rebuild
            // each sound's node chain on the new context and reconnect to the new master.
            sounds.forEach((s) => {
                try { SoundUtils.rebuildSoundNodes(s, newCtx, sm.destination); } catch (e) { /* ignore */ }
            });

            const startAll = () => {
                toReplay.forEach(({ s, volume }) => {
                    try {
                        s.play(volume !== undefined ? { loop: true, volume } : { loop: true });
                    } catch (e) { /* ignore */ }
                });
            };
            if (newCtx.state === 'running') {
                startAll();
            } else if (typeof newCtx.resume === 'function') {
                newCtx.resume().then(startAll).catch(startAll);
            } else {
                startAll();
            }
        } catch (e) {
            // ignore – best-effort recovery
        }
    }

    /**
     * Rebuilds a single WebAudioSound's node chain on a new context, mirroring what the
     * Phaser WebAudioSound constructor does, and reconnects it to the given master
     * destination. Clears stale buffer-source references so the next play() starts fresh.
     */
    private static rebuildSoundNodes(s: any, ctx: any, destination: any) {
        s.source = null;
        s.loopSource = null;
        s.isPlaying = false;
        s.isPaused = false;

        s.muteNode = ctx.createGain();
        s.volumeNode = ctx.createGain();
        s.pannerNode = null;
        s.spatialNode = null;

        s.muteNode.connect(s.volumeNode);

        if (ctx.createPanner) {
            s.spatialNode = ctx.createPanner();
            s.volumeNode.connect(s.spatialNode);
        }
        if (ctx.createStereoPanner) {
            s.pannerNode = ctx.createStereoPanner();
            if (ctx.createPanner) {
                s.spatialNode.connect(s.pannerNode);
            } else {
                s.volumeNode.connect(s.pannerNode);
            }
            s.pannerNode.connect(destination);
        } else if (ctx.createPanner) {
            s.spatialNode.connect(destination);
        } else {
            s.volumeNode.connect(destination);
        }
    }

    /**
     * Called once the context is confirmed `running` again on a non-interrupted cycle.
     * On mobile, rebuild any looping music whose buffer source the OS may have torn down
     * (Phaser still reports it as `isPlaying`, so it looks alive while silent). Desktop keeps
     * its sources across suspend/resume, so it is left alone to avoid an audible restart.
     */
    private static finishRecovery(soundManager: any) {
        if (!SoundUtils.pendingRecovery) {
            return;
        }
        // A context interrupted this cycle is a zombie — don't finish here; the next user
        // gesture recreates it. (Mirrors the guard in resumeGameAudioContext.)
        if (SoundUtils.sawInterrupted && SoundUtils.isProbablyMobile()) {
            return;
        }
        const context = soundManager?.context;
        if (context && context.state !== 'running') {
            // Not actually recovered yet; keep the flag so a later gesture retries.
            return;
        }
        SoundUtils.pendingRecovery = false;
        if (SoundUtils.isProbablyMobile()) {
            SoundUtils.restartLoopingSounds(soundManager);
        }
    }

    private static isProbablyMobile(): boolean {
        try {
            const ua = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
            if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
                return true;
            }
            // iPadOS 13+ reports a desktop Safari UA but exposes touch points.
            return typeof navigator !== 'undefined' && (navigator as any).maxTouchPoints > 1;
        } catch (e) {
            return false;
        }
    }

    /**
     * Stops and re-plays every looping sound Phaser still believes is playing (the music),
     * rebuilding buffer sources the OS tore down. The context is expected to be running by
     * the time this is called (see finishRecovery).
     */
    private static restartLoopingSounds(soundManager: any) {
        try {
            if (!soundManager || !Array.isArray(soundManager.sounds)) {
                return;
            }
            // Copy the array: stop()/play() can mutate the manager's sound list.
            soundManager.sounds.slice().forEach((s: any) => {
                try {
                    if (s && s.loop && s.isPlaying) {
                        s.stop();
                        s.play({ loop: true });
                    }
                } catch (ignore) {}
            });
        } catch (e) {
            // ignore – best-effort recovery
        }
    }

    public static setLooped(sound: BaseSound | undefined) {
        if (sound) {
            // @ts-ignore
            sound.loop = true;
        }
    }

    public static play(sound: BaseSound | undefined) {
        try {
            if (sound) {
                sound.play();
            }
        } catch (e) {
            console.log('catched play sound exception');
        }
    }

    public static playLooped(sound: BaseSound | undefined) {
        try {
            if (sound) {
                // @ts-ignore
                sound.loop = true;
                // @ts-ignore
                sound.play({ loop: true });
            }
        } catch (e) {
            console.log('catched playLooped sound exception');
        }
    }

    public static stop(sound: BaseSound | undefined) {
        try {
            if (sound) {
                sound.stop();
            }
        } catch (e) {
            console.log('catched stop sound exception');
        }
    }

    public static addAndPlay(scene: Scene, key: string, config: SoundConfig | undefined = undefined) {
        let sound = SoundUtils.addSound(scene, key, config);
        SoundUtils.play(sound);
    }

    public static addSound(scene: Scene, key: string, config: SoundConfig | undefined = undefined) {
        try {
            let res = scene.sound.get(key);
            if (res) {
                return res;
            }

            if (config) {
                return scene.sound.add(key, config);
            } else {
                return scene.sound.add(key);
            }
        } catch (e) {
            // console.log('catched sound exception during adding');
            // console.log(e);
            return undefined;
        }
    }

    public static getAttackSoundByPower(punchSounds: (Phaser.Sound.BaseSound | undefined)[], attackValue: number) {
        //Возвращает звук удара, в зависимости от силы атаки
        if (attackValue < 50) { return punchSounds[0]; }
        if (attackValue >= 50 && attackValue < 100) { return punchSounds[1]; }
        if (attackValue >= 100) { return punchSounds[2]; }
    }
}
